/** Score over time for one agent: overall, or one evaluator, per finished experiment. */

import { ChartTooltip } from "@/components/ui/chart-tooltip";
import { useState } from "react";
import { ChartLine } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Box } from "@/components/ui/box";
import { QueryState } from "@/components/ui/states";
import { formatDate, formatScore, plural } from "@/lib/format";
import { isLive } from "@/features/experiments/status";
import type { ExperimentSummary } from "@/features/experiments/types";
import { useAgentHistory } from "./hooks";
import { Select } from "@/components/ui/select";

/** Why the chart has no line yet, counting the experiments that did not finish. */
export function emptyChartText(experiments: Pick<ExperimentSummary, "status">[]): string {
  const failed = experiments.filter((e) => e.status === "failed").length;
  const running = experiments.filter((e) => isLive(e.status)).length;
  const parts = [
    failed > 0 && plural(failed, "failed experiment"),
    running > 0 && (failed > 0 ? `${running} running` : `${plural(running, "experiment")} running`),
  ].filter(Boolean);
  if (parts.length === 0) return "No finished experiments yet. Scores show here once one completes.";
  return `${parts.join(", ")}, none completed yet. Scores show here once one completes.`;
}

function ChartPlaceholder({ children }: { children: string }) {
  return (
    <div className="my-3 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-line bg-well px-4 py-7 text-center">
      <ChartLine size={18} strokeWidth={1.8} className="text-dim" aria-hidden />
      <p className="max-w-sm text-[13px] leading-relaxed text-muted">{children}</p>
    </div>
  );
}

export function ScoreChart({
  agentId,
  names,
  experiments = [],
}: {
  agentId: string;
  names: Record<string, string>;
  experiments?: Pick<ExperimentSummary, "status">[];
}) {
  const history = useAgentHistory(agentId);
  const [metric, setMetric] = useState("overall");
  const points = history.data ?? [];
  const keys = [...new Set(points.flatMap((p) => Object.keys(p.evaluators)))];
  const data = points.map((p) => ({
    label: formatDate(p.finished_at),
    name: p.name,
    value: metric === "overall" ? p.overall : (p.evaluators[metric] ?? null),
  }));

  return (
    <Box className="px-4 pt-3.5 pb-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Score over time</h2>
        {points.length > 0 && (
          <Select
            label="Metric to chart"
            buttonClassName="h-[30px] text-xs"
            value={metric}
            onChange={setMetric}
            options={[
              { value: "overall", label: "Overall" },
              ...keys.map((k) => ({ value: k, label: names[k] ?? k })),
            ]}
          />
        )}
      </div>
      <QueryState query={history} what="score history">
        {points.length < 2 ? (
          <ChartPlaceholder>
            {points.length === 0
              ? emptyChartText(experiments)
              : "Only one finished experiment so far. Run another to see a trend."}
          </ChartPlaceholder>
        ) : (
          <div role="img" aria-label={`Score across ${points.length} experiments`} className="h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 16, right: 16, bottom: 0, left: -12 }}>
                <CartesianGrid stroke="var(--color-line-soft)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "var(--color-faint)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[0, 1]}
                  tick={{ fontSize: 11, fill: "var(--color-faint)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <ChartTooltip
                  formatter={(v) => formatScore(typeof v === "number" ? v : null)}
                  labelFormatter={(_, p) => (p[0]?.payload as { name?: string } | undefined)?.name ?? ""}
                />
                <Line
                  type="linear"
                  dataKey="value"
                  stroke="var(--color-ink)"
                  strokeWidth={1.8}
                  dot={{ r: 4, fill: "var(--color-ink)" }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </QueryState>
    </Box>
  );
}
